import { useEffect, useState } from "react";
import { useBrowserWallet } from "@nullaone/wallet-react";
import { fetchMyRole } from "../api/admin-api";
import type { Role } from "../types/admin";

export interface AdminGuardState {
  /** Connected wallet's KV role; "anonymous" until resolved / when no wallet. */
  role: Role;
  /** operator or admin — may view admin surfaces + ops worklist. */
  hasAccess: boolean;
  /** admin only — user management. */
  isAdmin: boolean;
  /** Header shows the Admin nav entry (same as hasAccess). */
  navVisible: boolean;
  /** Role probe in flight. */
  isLoading: boolean;
}

/**
 * useAdminGuard — resolves the connected wallet's KV role via
 * GET /api/users/me/role and derives access flags.
 *
 * 2026-06-12 admin-auth alignment (mirrors detrust-oracle's wallet-role
 * guard). No password, no admin token: access == having an operator/admin
 * role granted to your wallet. Re-probes on wallet change.
 */
export function useAdminGuard(): AdminGuardState {
  const { publicKey } = useBrowserWallet();
  const [role, setRole] = useState<Role>("anonymous");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!publicKey) {
      setRole("anonymous");
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchMyRole()
      .then((r) => {
        if (!cancelled) setRole(r);
      })
      .catch(() => {
        if (!cancelled) setRole("anonymous");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [publicKey]);

  const hasAccess = role === "admin" || role === "operator";
  return {
    role,
    hasAccess,
    isAdmin: role === "admin",
    navVisible: hasAccess,
    isLoading,
  };
}
